import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import logError from '@alltrails/core/utils/logError';
import { useAppDispatch, useAppSelector } from '../../redux';
import { setCostingsConfig, setToastAlerts, setToastMessage } from '../../redux/customRoute';
import { useGetInfoQuery, useLazyGetRouteQuery } from '../../redux/customRoutesApi';
import type { RoutingMode, ServerRoute } from '../../redux/customRoutesApi';
import { ActivityType, Costings } from '../../types/CustomRoute';
import { getGeneralError } from '../../utils/customRoutes/constants';

type RoutingOptions = {
  newActivity?: ActivityType;
  routes: ServerRoute[];
  costings?: Costings;
  onFetch?: () => void;
};

export const activityToRoutingMode: Record<ActivityType, RoutingMode> = {
  [ActivityType.Hiking]: 'hike',
  [ActivityType.RoadBiking]: 'bike',
  [ActivityType.MountainBiking]: 'mtb',
  [ActivityType.OffRoadDriving]: 'offroad',
  [ActivityType.ScenicDriving]: 'car'
};

const useRouting = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { activity, costingsConfig } = useAppSelector(state => ({
    activity: state.customRoute.route.activityType,
    costingsConfig: state.customRoute.costingsConfig
  }));
  const routingMode = useMemo(() => activityToRoutingMode[activity] || 'hike', [activity]);

  // Ref to store the isFetching state to minimize dependencies in the routing function
  const isFetchingRef = useRef(false);

  const { isSuccess: isInfoSuccess, data: infoData } = useGetInfoQuery();
  const [fetch] = useLazyGetRouteQuery();

  const routing = useCallback(
    async ({ routes, costings, newActivity, onFetch }: RoutingOptions) => {
      if (isFetchingRef.current) {
        return { isSuccess: false };
      }

      isFetchingRef.current = true;
      onFetch?.();

      const activeRoutingMode = newActivity ? activityToRoutingMode[newActivity] : routingMode;

      const {
        isSuccess: isQuerySuccess,
        isError,
        data: queryData,
        error
      } = await fetch(
        {
          routingMode: activeRoutingMode,
          routes,
          activityTypeChange: activeRoutingMode !== routingMode,
          ...(costings ? { costings } : {})
        },
        true // preferCacheValue
      );
      let isSuccess = isQuerySuccess;
      let data = queryData;

      if (isError) {
        dispatch(setToastMessage(getGeneralError(intl)));
        logError(new Error('Failed to fetch routing data', { cause: error }));
      }

      if (isQuerySuccess && queryData?.error) {
        isSuccess = false;
        data = undefined;
        const { iconCode, message } = queryData.error;
        dispatch(setToastMessage({ iconCode, message }));
      }

      if (isQuerySuccess && queryData?.alerts?.length) {
        dispatch(setToastAlerts([...queryData.alerts]));
      }

      isFetchingRef.current = false;
      return { isSuccess, data };
    },
    [routingMode, dispatch, fetch, intl]
  );

  useEffect(() => {
    // Since the info query has no params we only need to set the costingsConfig once across all instances of useRouting
    if (!costingsConfig && isInfoSuccess && infoData) {
      dispatch(setCostingsConfig(infoData.costings));
    }
  }, [costingsConfig, isInfoSuccess, infoData, dispatch]);

  return routing;
};

export default useRouting;
